import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SectionHeader from "../common/SectionHeader";
import AdDeliverList from "./AdDeliverList";
import NewDeliveryChoiceModal from "./Modals/NewDeliveryChoiceModal";
import NewDeliveryModal from "./Modals/NewDeliveryModal";
import BulkUploadExcelModal from "./Modals/BulkUploadExcelModal";
import AdDeliverDetailModal from "./Modals/AdDeliverDetailModal";
import ResubmitAssetModal from "./Modals/ResubmitAssetModal";
import DirectDeliveryModal from "./Modals/DirectDeliveryModal";
import CreateAdRequestChoiceModal from "../AdRequest/Modals/CreateAdRequestChoiceModal";
import UploadExcelModal from "../AdRequest/Modals/UploadExcelModal";
import AdRequestSheetModal from "../AdRequest/Modals/AdRequestSheetModal";
import { Button } from "../ui/button";
import usePermissions from "../../hooks/usePermissions";
import {
  AGENCY_LIST_PERMISSIONS,
  PERMISSION_CODES,
} from "../../utils/constants/permissions";
import useZustandStore from "../../zustandStore/useZustandStore";
import { isAgencyScopedUser } from "../../utils/agencyScope";

const DIRECT_CHOICE_LABELS = {
  bulk: "Deliver in bulk",
  sheet: "Deliver from Google Sheets",
  single: "Deliver a single request",
};

const isRejectedQuery = (params) => {
  const v = (params.get("rejected") || "").toLowerCase();
  return v === "1" || v === "true" || v === "yes";
};

const AdDeliverSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rejectedOnly = isRejectedQuery(searchParams);
  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [singleModalOpen, setSingleModalOpen] = useState(false);
  const [bulkExcelOpen, setBulkExcelOpen] = useState(false);
  const [detailDeliveryId, setDetailDeliveryId] = useState(null);
  const [resubmitInitial, setResubmitInitial] = useState(null);
  // Set by a row's Deliver button: skips the chooser, prefills the form.
  const [deliverRequestId, setDeliverRequestId] = useState(null);
  const [resubmitDeliveryFromDetail, setResubmitDeliveryFromDetail] =
    useState(null);
  const { hasPermission } = usePermissions();
  const canCreateDelivery = hasPermission(PERMISSION_CODES.AD_DELIVER_CREATE);
  const isLoggedIn = !!useZustandStore((s) => s?.currentUser?.roles);
  const currentUser = useZustandStore((s) => s?.currentUser);
  // null | "choice" | "single" | "bulk" | "sheet"
  const [directMode, setDirectMode] = useState(null);
  // Agency users need the agency switch; Seekho staff pick a switched-on agency in the form.
  const canDeliverDirect =
    canCreateDelivery &&
    (isAgencyScopedUser(currentUser)
      ? !!currentUser?.agency_allow_direct_delivery
      : AGENCY_LIST_PERMISSIONS.some((p) => hasPermission(p)));

  useEffect(() => {
    const openDelivery = searchParams.get("open") === "delivery";
    if (!openDelivery) {
      setChoiceModalOpen(false);
      return;
    }
    if (!isLoggedIn) return;
    if (!canCreateDelivery) {
      setChoiceModalOpen(false);
      setSearchParams((p) => {
        const next = new URLSearchParams(p);
        next.delete("open");
        const s = next.toString();
        return s ? next : new URLSearchParams();
      });
      return;
    }
    setChoiceModalOpen(true);
  }, [searchParams, canCreateDelivery, isLoggedIn, setSearchParams]);

  return (
    <section className="p-4 md:p-6">
      {!rejectedOnly && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <SectionHeader title="Ad Deliver" />
          <div className="flex flex-wrap items-center gap-2">
            {canDeliverDirect && (
              <Button variant="outline" onClick={() => setDirectMode("choice")}>
                Deliver without request
              </Button>
            )}
            {canCreateDelivery && (
              <Button
                onClick={() => {
                  setSearchParams((p) => {
                    const next = new URLSearchParams(p);
                    next.set("open", "delivery");
                    return next;
                  });
                  setChoiceModalOpen(true);
                }}
              >
                New delivery
              </Button>
            )}
          </div>
        </div>
      )}
      <AdDeliverList
        rejectedOnly={rejectedOnly}
        onOpenDelivery={(id) => setDetailDeliveryId(id)}
        onDeliverRequest={(requestId) => {
          setDeliverRequestId(requestId);
          setSingleModalOpen(true);
        }}
      />
      <AdDeliverDetailModal
        open={!!detailDeliveryId}
        deliveryId={detailDeliveryId}
        onClose={() => setDetailDeliveryId(null)}
        onResubmit={(detail) => {
          setDetailDeliveryId(null);
          setResubmitDeliveryFromDetail(detail);
        }}
      />
      <NewDeliveryChoiceModal
        open={choiceModalOpen}
        onClose={() => {
          setChoiceModalOpen(false);
          setSearchParams((p) => {
            const next = new URLSearchParams(p);
            next.delete("open");
            const s = next.toString();
            return s ? next : new URLSearchParams();
          });
        }}
        onSingleEntry={() => setSingleModalOpen(true)}
        onUploadExcel={() => setBulkExcelOpen(true)}
      />
      <NewDeliveryModal
        open={singleModalOpen}
        onClose={() => {
          setSingleModalOpen(false);
          setResubmitInitial(null);
          setDeliverRequestId(null);
        }}
        resubmitInitial={resubmitInitial}
        initialRequestId={deliverRequestId}
      />
      <BulkUploadExcelModal
        open={bulkExcelOpen}
        onClose={() => setBulkExcelOpen(false)}
      />
      <CreateAdRequestChoiceModal
        open={directMode === "choice"}
        onClose={() => setDirectMode((m) => (m === "choice" ? null : m))}
        title="Deliver without request"
        labels={DIRECT_CHOICE_LABELS}
        onCreateSingle={() => setDirectMode("single")}
        onUploadExcel={() => setDirectMode("bulk")}
        onSheetImport={() => setDirectMode("sheet")}
      />
      {directMode === "single" && (
        <DirectDeliveryModal open onClose={() => setDirectMode(null)} />
      )}
      {directMode === "bulk" && (
        <UploadExcelModal direct open onClose={() => setDirectMode(null)} />
      )}
      {directMode === "sheet" && (
        <AdRequestSheetModal direct open onClose={() => setDirectMode(null)} />
      )}
      <ResubmitAssetModal
        open={!!resubmitDeliveryFromDetail}
        onClose={() => setResubmitDeliveryFromDetail(null)}
        delivery={resubmitDeliveryFromDetail}
      />
    </section>
  );
};

export default AdDeliverSection;
